import React from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { Flyout } from '@els/els-react--flyout';
import { ButtonWithIcon } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';
import InnerHTML from '@Components/InnerHTML';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import Button from '@Components/Button';

import './document-bibliographic-info.component.theme.scss';

const safeArray = (input) => [].concat(input);

function DocumentAssigneesInventorsTableOld({ citation }) {
  const { assignees, inventors } = citation || {};
  return assignees || inventors ? (
    <table className="rx-table rx-table--full-width rx-table--fixed">
      <colgroup>
        <col className="rx-table__col--half" />
        <col className="rx-table__col--half" />
      </colgroup>
      <thead>
        <tr>
          <th scope="col">Assignees</th>
          <th scope="col">Inventors (Authors)</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <InnerHTML tag="td" html={assignees} />
          {/* eslint-disable-next-line custom/no-unsafe-deep-access */}
          <InnerHTML tag="td" html={safeArray(inventors).join('; ')} />
        </tr>
      </tbody>
    </table>
  ) : null;
}

function DocumentAssigneesInventorsTableNew({ citation = {} }) {
  const { assignees, originalAssignee, inventors, ultimateOwner } = citation || {};
  return (
    <>
      {(assignees || originalAssignee || ultimateOwner) && (
        <table
          className="rx-table rx-table--full-width bibliographic-info-table bibliographic-info-table--assignees"
          data-testid="bibliographic-info-table-assignees">
          <tbody>
            {assignees && (
              <tr>
                <th>
                  <div className="bibliographic-info-table__fixed-content">Current Patent Assignee</div>
                </th>
                <InnerHTML tag="td" html={assignees} />
              </tr>
            )}
            {ultimateOwner && (
              <tr data-testid="bibliographic-info-ultimateOwner">
                <th className="bibliographic-info__ultimateOwner">
                  <div className="bibliographic-info-table__fixed-content">
                    <span>Ultimate Owner</span>
                    <Flyout
                      className="bibliographic-info__ultimate-owner-tooltip"
                      flyout={
                        <div className="bibliographic-info__tooltip-content u-els-font-size-base u-els-color-text">
                          <div className="u-els-margin-bottom">
                            The ultimate owner is the harmonized legal entity that ultimately benefits from owning the rights to a patent.
                          </div>
                          <LinkElement
                            href="https://service.elsevier.com/app/answers/detail/a_id/37122/supporthub/reaxys/"
                            className="e2e-bibliographic-info-ultimate-owner-link"
                            isExternal>
                            Learn more
                          </LinkElement>
                        </div>
                      }
                      shouldCloseOnESC
                      showCloseButton
                      placement="bottom-start"
                      pointerType="primary">
                      <ButtonWithIcon
                        sprite="InformationOutlineCircle"
                        iconOnly
                        type="link"
                        linkIconSize="xs"
                        className="e2e-bibliographic-info-ultimate-owner"
                        onClick={noop}>
                        Ultimate Owner
                      </ButtonWithIcon>
                    </Flyout>
                  </div>
                </th>
                <InnerHTML tag="td" html={ultimateOwner} />
              </tr>
            )}
            {originalAssignee && (
              <tr>
                <th>
                  <div className="bibliographic-info-table__fixed-content">Original Patent Assignee</div>
                </th>
                <InnerHTML tag="td" html={originalAssignee} />
              </tr>
            )}
          </tbody>
        </table>
      )}
      {inventors && (
        <table
          className="rx-table rx-table--full-width bibliographic-info-table bibliographic-info-table--inventors"
          data-testid="bibliographic-info-table-inventors">
          <tbody>
            <tr>
              <th>
                <div className="bibliographic-info-table__fixed-content">Inventors</div>
              </th>
              {/* eslint-disable-next-line custom/no-unsafe-deep-access */}
              <InnerHTML tag="td" html={safeArray(inventors).join('; ')} />
            </tr>
          </tbody>
        </table>
      )}
    </>
  );
}

function DocumentPatentFamilyTable({ citation }) {
  const { patentFamily } = citation || {};
  return patentFamily ? (
    <table
      className="rx-table rx-table--full-width bibliographic-info-table bibliographic-info-table--patent-family"
      data-testid="bibliographic-info-table-patent-family">
      <thead>
        <tr>
          <th scope="col">
            <div className="bibliographic-info-table__fixed-content">Patent Number</div>
          </th>
          <th scope="col">Kind Code</th>
          <th scope="col">Publication Date</th>
          <th scope="col">Filing Date</th>
        </tr>
      </thead>
      <tbody>
        {patentFamily.map((patent, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={index}>
            <InnerHTML tag="td" html={patent.patentNo} />
            <InnerHTML tag="td" html={patent.kindCode} />
            <InnerHTML tag="td" html={patent.publicationDate} />
            <InnerHTML tag="td" html={patent.fillingDate} />
          </tr>
        ))}
      </tbody>
    </table>
  ) : null;
}

function DocumentPriorityDataTable({ citation }) {
  const { priorityData } = citation || {};
  return priorityData ? (
    <table className="rx-table rx-table--full-width bibliographic-info-table bibliographic-info-table--priority-data">
      <thead>
        <tr>
          <th scope="col">
            <div className="bibliographic-info-table__fixed-content">Priority No</div>
          </th>
          <th scope="col">Priority Date</th>
        </tr>
      </thead>
      <tbody>
        {priorityData.map((data, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={index}>
            <InnerHTML tag="td" html={data.number} />
            <InnerHTML tag="td" html={data.date} />
          </tr>
        ))}
      </tbody>
    </table>
  ) : null;
}

function DocumentPatentClassificationTable({ citation }) {
  const { mainIpc, secondaryIpc } = citation || {};
  return mainIpc || secondaryIpc ? (
    <table className="rx-table rx-table--full-width bibliographic-info-table bibliographic-info-table--patent-classification">
      <thead>
        <tr>
          <th scope="col">
            <div className="bibliographic-info-table__fixed-content">Patent Classification</div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Main IPC</td>
          {/* eslint-disable-next-line custom/no-unsafe-deep-access */}
          <InnerHTML tag="td" html={safeArray(mainIpc).join('; ')} />
        </tr>
        <tr>
          <td>Secondary IPC</td>
          {/* eslint-disable-next-line custom/no-unsafe-deep-access */}
          <InnerHTML tag="td" html={safeArray(secondaryIpc).join('; ')} />
        </tr>
      </tbody>
    </table>
  ) : null;
}

function DocumentBibliographicInfoComponent({ document: citation, closeClick }) {
  const patentSightEnabled = !Auth.isFeatureEnabled(FEATURES.disablePatentSightUIChanges);

  return (
    <div className="result-detail-panel" data-testid="bibliographic-info-root">
      <div className="result-detail-panel-wrapper">
        <Button
          buttonConfig={{
            type: 'link',
            size: 'md',
            className: 'els-btn-modal-close',
            ariaLabel: 'Close',
            e2eName: 'close',
          }}
          iconConfig={{ name: 'delete', size: 'tiny' }}
          onClick={closeClick}
        />
        <h4>{patentSightEnabled ? 'Bibliographic Info' : 'Front page info'}</h4>
        <div className="result-detail-block front-page-info">
          {patentSightEnabled ? (
            <DocumentAssigneesInventorsTableNew citation={citation} />
          ) : (
            <DocumentAssigneesInventorsTableOld citation={citation} />
          )}
          <DocumentPatentFamilyTable citation={citation} />
          <DocumentPriorityDataTable citation={citation} />
          <DocumentPatentClassificationTable citation={citation} />
        </div>
      </div>
    </div>
  );
}

DocumentBibliographicInfoComponent.propTypes = {
  document: PropTypes.object.isRequired,
  closeClick: PropTypes.func.isRequired,
};

export default DocumentBibliographicInfoComponent;
